import type { AlertBillView, MonthView } from '../api.js'
import { cents, esc, isNegative, isPositive, money, money2, monthLong, shortDate } from '../format.js'

/**
 * The header, banners, Investigate chip and the four summary tiles.
 *
 * Markup carried over from the prototype unchanged — Amy approved this layout.
 * What has changed is where the numbers come from: every figure below is
 * computed on the server and arrives already formatted as a decimal string.
 */

const $ = <T extends HTMLElement>(selector: string): T =>
  document.querySelector<T>(selector) as T

function percent(part: string, whole: string): number {
  const total = cents(whole)
  if (total <= 0) return cents(part) > 0 ? 100 : 0
  return Math.min(100, (cents(part) / total) * 100)
}

export function renderHeader(
  view: MonthView,
  canWrite: boolean,
  { canClose = false, canReopen = false }: { canClose?: boolean; canReopen?: boolean } = {},
): void {
  $('#monthLabel').textContent = monthLong(view.month)

  const banners: string[] = []

  const alertBanner = renderBillAlert(view)
  if (alertBanner) banners.push(alertBanner)

  if (view.containsSampleData) {
    banners.push(
      `<div class="banner"><span><b>Sample data is showing.</b> When you start entering ` +
        `real numbers, clear it. Your own entries, and the weekly charge settings, stay.</span>` +
        (canWrite ? '<button class="btn small" id="clearEx">Clear all sample data</button>' : '') +
        `</div>`,
    )
  }

  if (view.closed) {
    banners.push(
      `<div class="banner info"><span><b>${esc(monthLong(view.month))} is closed.</b> ` +
        `Its report has been frozen exactly as it was sent. An owner can reopen it if ` +
        `something needs correcting.</span>` +
        (canReopen ? '<button class="btn small" id="reopenMonth">Reopen month</button>' : '') +
        `</div>`,
    )
  } else if (canClose) {
    banners.push(
      `<div class="banner info"><span>When ${esc(monthLong(view.month))} is finished and the ` +
        `report has gone to the accountant, close it to freeze the figures.</span>` +
        `<button class="btn small" id="closeMonth">Close month</button></div>`,
    )
  }

  if (!canWrite) {
    banners.push(
      `<div class="banner info"><span>You can view this budget. Ask an owner for edit ` +
        `access to make changes.</span></div>`,
    )
  }

  $('#banner').innerHTML = banners.join('')

  for (const button of document.querySelectorAll<HTMLElement>('[data-add]')) {
    button.hidden = !canWrite || view.closed
  }
}

export function renderInvestigateChip(view: MonthView): void {
  const chip = $('#invChip')
  const { total, count } = view.summary.investigate

  chip.className = `inv${count ? '' : ' clear'}`
  chip.innerHTML = count
    ? `<span class="q">?</span>Investigate <b>${money2(total)}</b> · ${count} unidentified`
    : `<span class="q">✓</span>Nothing to investigate`
}

export function renderSummary(view: MonthView): void {
  const { revenue } = view.summary

  const incomePercent = percent(revenue.recurringReceived, revenue.recurringMonthly)
  const spendPercent = percent(view.summary.spent, view.summary.budgeted)
  const overBudget = isNegative(view.summary.budgetRemaining)
  const barClass = overBudget ? 'bad' : spendPercent > 85 ? 'warn' : ''

  // Negative outstanding means more came in than the base, which reads as good
  // news rather than a shortfall.
  const outstandingIsShortfall = isPositive(revenue.recurringOutstanding)
  const outstandingLabel = outstandingIsShortfall
    ? 'Recurring Revenue Outstanding'
    : isNegative(revenue.recurringOutstanding)
      ? 'Recurring received above base'
      : 'Recurring Revenue Outstanding'
  const outstandingAmount = revenue.recurringOutstanding.replace('-', '')

  $('#summary').innerHTML = `
    <div class="tile"><span class="lbl">Income Received / Expected</span>
      <span class="big num">${money(revenue.recurringReceived)} <span class="of">/ ${money(revenue.recurringMonthly)}</span></span>
      <div class="bar"><i style="width:${incomePercent}%"></i></div>
      <div class="revrows">
        <div><span>Recurring Monthly Revenue</span><b>${revenue.rmrSet ? money(revenue.recurringMonthly) : 'not set'}</b></div>
        <div><span>Recurring Revenue Received</span><b>${money(revenue.recurringReceived)}</b></div>
        <div class="${outstandingIsShortfall ? 'out' : ''}"><span>${outstandingLabel}</span><b>${money(outstandingAmount)}</b></div>
        <div><span>Additional Revenue Received</span><b>${money(revenue.additionalReceived)}</b></div>
        <div class="tot"><span>Total Revenue Received</span><b>${money(revenue.totalReceived)}</b></div>
      </div>
      ${isPositive(revenue.transfers) ? `<span class="sub">${money(revenue.transfers)} in transfers between NI accounts isn't counted.</span>` : ''}</div>
    <div class="tile"><span class="lbl">Spent / Budgeted</span>
      <span class="big num">${money(view.summary.spent)} <span class="of">/ ${money(view.summary.budgeted)}</span></span>
      <div class="bar"><i class="${barClass}" style="width:${spendPercent}%"></i></div>
      <div class="split"><span>${overBudget ? 'Over budget' : 'Remaining'}</span><b>${money(view.summary.budgetRemaining.replace('-', ''))}</b></div></div>
    <div class="tile"><span class="lbl">Payroll &amp; owner pay</span><span class="big num">${money(view.summary.payrollScheduled)}</span>
      <span class="sub">scheduled this month</span></div>
    <div class="tile hero"><span class="lbl">Left after plan</span><span class="big num">${money(view.summary.leftAfterPlan)}</span>
      <span class="sub">recurring monthly revenue − budget − payroll</span></div>`
}

/**
 * The bill reminder.
 *
 * Bills go out on Friday, so the useful moment is Thursday — while there is
 * still time to move money. Overdue bills lead, because they matter more than
 * anything merely approaching.
 */
function renderBillAlert(view: MonthView): string | null {
  const alert = view.alert
  if (!alert?.enabled) return null
  if (alert.upcoming.length === 0 && alert.overdue.length === 0) return null

  const when = (bill: AlertBillView) =>
    bill.daysAway === 0
      ? 'today'
      : bill.daysAway === 1
        ? 'tomorrow'
        : `in ${bill.daysAway} days`

  const line = (bill: AlertBillView, overdue: boolean) =>
    `<li><span class="alert-when">${
      overdue ? `${Math.abs(bill.daysAway)}d late` : esc(when(bill))
    }</span>` +
    `<span class="alert-name">${esc(bill.name)}${
      bill.autopay ? ' <span class="tag">auto</span>' : ''
    }</span>` +
    `<span class="alert-due">${esc(shortDate(bill.dueDate))}</span>` +
    `<b class="num">${money2(bill.amount)}</b></li>`

  const sections: string[] = []

  if (alert.overdue.length > 0) {
    sections.push(
      `<div class="alert-block late"><b>${alert.overdue.length} overdue · ${money(
        alert.overdueTotal,
      )}</b><ul class="alert-list">${alert.overdue
        .map((bill) => line(bill, true))
        .join('')}</ul></div>`,
    )
  }

  if (alert.upcoming.length > 0) {
    sections.push(
      `<div class="alert-block"><b>${alert.upcoming.length} due in the next ${
        alert.leadDays
      } days · ${money(alert.upcomingTotal)}</b><ul class="alert-list">${alert.upcoming
        .map((bill) => line(bill, false))
        .join('')}</ul></div>`,
    )
  }

  // On the chosen day this is the reason to have opened the app at all.
  const heading = alert.isAlertDay
    ? "<b>Bills to get ahead of.</b> They go out tomorrow."
    : '<b>Coming up.</b>'

  return `<div class="banner alert${alert.isAlertDay ? ' today' : ''}">
    <div class="alert-body"><span>${heading}</span>${sections.join('')}</div></div>`
}
